import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const DataTable = ({ 
  data, 
  headers, 
  onUpdate, 
  onView, 
  onDelete, 
  canUpdate = false, 
  canDelete = false,
  loading = false,
  renderCell = null,
  customActions = null,
  actionColumnLabel = 'Actions',
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const getHeaderLabel = (header) => {
    if (typeof header === 'string') {
      return header.charAt(0).toUpperCase() + header.slice(1);
    }
    if (typeof header === 'object' && header.label) {
      return header.label;
    }
    return header;
  };

  const getHeaderKey = (header) => {
    if (typeof header === 'string') {
      return header;
    }
    if (typeof header === 'object' && header.key) {
      return header.key;
    }
    return header;
  };

  const hasCustomActions = !!customActions;
  const hasDefaultActions = onView || canUpdate || canDelete;
  const showActions = hasCustomActions || hasDefaultActions;
  console.log('data', data)

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0 }}>
      <Table size="small">
        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
          <TableRow>
            {headers?.map((header, index) => (
              <TableCell 
                key={index} 
                sx={{ 
                  fontWeight: 600, 
                  color: '#475467', 
                  fontSize: '0.7rem',
                  py: 1,
                  px: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                }}
              >
                {getHeaderLabel(header)}
              </TableCell>
            ))}
            {showActions && (
              <TableCell 
                sx={{ 
                  fontWeight: 600, 
                  color: '#475467', 
                  fontSize: '0.7rem',
                  py: 1,
                  px: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  textAlign: 'center',
                }}
              >
                {actionColumnLabel}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data && data?.length > 0 ? (
            data?.map((item, index) => (
              <TableRow 
                key={item.id || index} 
                hover
                sx={{
                  '&:last-child td': { borderBottom: 0 },
                  '&:hover': { bgcolor: alpha('#2469F0', 0.02) },
                }}
              >
                {headers?.map((header) => {
                  const key = getHeaderKey(header);
                  return (
                    <TableCell 
                      key={key} 
                      sx={{ 
                        color: '#1A1A2E', 
                        fontSize: '0.78rem',
                        py: 0.75,
                        px: 1.5,
                        borderBottom: '1px solid #F2F4F7',
                      }}
                    >
                      {renderCell ? renderCell(item, header) : (item[key] || '-')}
                    </TableCell>
                  );
                })}
                {showActions && (
                  <TableCell 
                    sx={{ 
                      py: 0.75,
                      px: 1.5,
                      borderBottom: '1px solid #F2F4F7',
                      textAlign: 'center',
                    }}
                  >
                    {hasCustomActions ? (
                      customActions(item)
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                        {onView && (
                          <Tooltip title="View" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onView(item.id)}
                              sx={{ 
                                color: '#667085', 
                                p: 0.5,
                                '&:hover': { 
                                  color: '#2469F0',
                                  bgcolor: alpha('#2469F0', 0.06),
                                } 
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canUpdate && (
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onUpdate(item.id)}
                              sx={{ 
                                color: '#667085', 
                                p: 0.5,
                                '&:hover': { 
                                  color: '#F59E0B',
                                  bgcolor: alpha('#F59E0B', 0.06),
                                } 
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onDelete(item.id)}
                              sx={{ 
                                color: '#667085', 
                                p: 0.5,
                                '&:hover': { 
                                  color: '#EF4444',
                                  bgcolor: alpha('#EF4444', 0.06),
                                } 
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell 
                colSpan={headers?.length + (showActions ? 1 : 0)} 
                sx={{ 
                  textAlign: 'center', 
                  py: 3,
                  color: '#98A2B3',
                  fontSize: '0.85rem',
                }}
              >
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;